import { Temporal } from '@js-temporal/polyfill';
import { useEffect, useRef, useState, type MouseEvent, type PointerEvent } from 'react';

// CB-REQ OPS-03 at v1.9105, CB-CONV §12 crew part 4C-2: drawing, moving and resizing on a timeline,
// as one shared piece. It knows tracks (a row a window can be drawn on) and bars (a window that can
// move or change an end), and reports what was drawn, moved or resized; what each means, and
// whether to ask before saving, is the page's. The roster uses it first, the job page's schedule
// next (docs/designs/job-page.md).
//
// A press and hold starts a drag, so a click still opens a bar; movement before the hold is a
// click or a scroll, not a drag. Drawing on an empty track needs no hold: a click there does
// nothing else. Touch never drags (on a phone the timeline scrolls under the finger), and a
// keyboard or screen reader keeps the bar's own popover (WCAG 2.5.7).

export interface GestureWindow {
  readonly startsAt: Temporal.Instant;
  readonly endsAt: Temporal.Instant;
}

export type Edge = 'start' | 'end';

export type Gesture =
  | { readonly kind: 'draw'; readonly track: string; readonly window: GestureWindow }
  | {
      readonly kind: 'move';
      readonly id: string;
      readonly from: GestureWindow;
      readonly window: GestureWindow;
    }
  | {
      readonly kind: 'resize';
      readonly id: string;
      readonly edge: Edge;
      readonly from: GestureWindow;
      readonly window: GestureWindow;
    };

/** The press that arms a drag on a bar, in milliseconds, and the movement that cancels one first. */
export const HOLD_MS = 300;
const SLOP_PX = 4;
/** How near a bar's end a press resizes it rather than moving it, in pixels. */
const EDGE_PX = 6;

const MINUTE = 60_000;

/** The instant at a fraction (0 – 1) of the span. */
export function instantAt(
  span: { from: Temporal.Instant; to: Temporal.Instant },
  fraction: number,
): Temporal.Instant {
  const whole = span.to.epochMilliseconds - span.from.epochMilliseconds;
  const clamped = Math.min(1, Math.max(0, fraction));
  return Temporal.Instant.fromEpochMilliseconds(
    span.from.epochMilliseconds + Math.round(whole * clamped),
  );
}

/** The nearest `snapMinutes` on the zone's wall clock: 15 for a quarter hour, 60 for an hour. */
export function snapTo(
  instant: Temporal.Instant,
  zone: string,
  snapMinutes: number,
): Temporal.Instant {
  const zoned = instant.toZonedDateTimeISO(zone);
  const rounded =
    snapMinutes >= 60
      ? zoned.round({ smallestUnit: 'hour', roundingMode: 'halfExpand' })
      : zoned.round({
          smallestUnit: 'minute',
          roundingIncrement: snapMinutes,
          roundingMode: 'halfExpand',
        });
  return rounded.toInstant();
}

const later = (a: Temporal.Instant, minutes: number) =>
  Temporal.Instant.fromEpochMilliseconds(a.epochMilliseconds + minutes * MINUTE);

/** A window drawn between two fractions of the span, either way round, snapped, never too short. */
export function drawnWindow(
  span: { from: Temporal.Instant; to: Temporal.Instant },
  zone: string,
  snapMinutes: number,
  shortestMinutes: number,
  a: number,
  b: number,
): GestureWindow {
  const startsAt = snapTo(instantAt(span, Math.min(a, b)), zone, snapMinutes);
  let endsAt = snapTo(instantAt(span, Math.max(a, b)), zone, snapMinutes);
  if (endsAt.epochMilliseconds - startsAt.epochMilliseconds < shortestMinutes * MINUTE) {
    endsAt = later(startsAt, shortestMinutes);
  }
  return { startsAt, endsAt };
}

/** A window moved by a distance: its start snapped, its length kept. */
export function movedWindow(
  window: GestureWindow,
  deltaMs: number,
  zone: string,
  snapMinutes: number,
): GestureWindow {
  const length = window.endsAt.epochMilliseconds - window.startsAt.epochMilliseconds;
  const startsAt = snapTo(
    Temporal.Instant.fromEpochMilliseconds(window.startsAt.epochMilliseconds + deltaMs),
    zone,
    snapMinutes,
  );
  return {
    startsAt,
    endsAt: Temporal.Instant.fromEpochMilliseconds(startsAt.epochMilliseconds + length),
  };
}

/** One end moved to an instant, snapped, never closer to the other than the shortest. */
export function resizedWindow(
  window: GestureWindow,
  edge: Edge,
  to: Temporal.Instant,
  zone: string,
  snapMinutes: number,
  shortestMinutes: number,
): GestureWindow {
  const snapped = snapTo(to, zone, snapMinutes);
  if (edge === 'end') {
    const floor = later(window.startsAt, shortestMinutes);
    return {
      startsAt: window.startsAt,
      endsAt: Temporal.Instant.compare(snapped, floor) < 0 ? floor : snapped,
    };
  }
  const ceiling = later(window.endsAt, -shortestMinutes);
  return {
    startsAt: Temporal.Instant.compare(snapped, ceiling) > 0 ? ceiling : snapped,
    endsAt: window.endsAt,
  };
}

export interface TimelineGestureOptions {
  readonly from: Temporal.Instant;
  readonly to: Temporal.Instant;
  readonly zone: string;
  readonly snapMinutes: number;
  readonly shortestMinutes: number;
  /** Off, nothing draws or drags: a phone, Select mode, someone who may not change it. */
  readonly enabled: boolean;
  readonly onDraw: (track: string, window: GestureWindow) => void;
  readonly onMove: (id: string, from: GestureWindow, window: GestureWindow) => void;
  readonly onResize: (id: string, edge: Edge, from: GestureWindow, window: GestureWindow) => void;
}

/** What a bar allows. */
export interface BarCan {
  readonly move: boolean;
  readonly resize: boolean;
}

/** Where a pointer is along its track, 0 – 1. */
function fractionOn(track: Element, clientX: number): number {
  const rect = track.getBoundingClientRect();
  return rect.width > 0 ? (clientX - rect.left) / rect.width : 0;
}

/**
 * Draw on a track, move or resize a bar. Spread `trackProps(id)` on each track element and
 * `barProps(id, window, can)` on each bar; render `live` as the ghost and the time chip.
 */
export function useTimelineGestures(options: TimelineGestureOptions) {
  const [live, setLive] = useState<Gesture | null>(null);
  const opts = useRef(options);
  opts.current = options;
  const swallowClick = useRef(false);
  const stop = useRef<(() => void) | null>(null);
  useEffect(() => () => stop.current?.(), []);

  const span = () => ({ from: opts.current.from, to: opts.current.to });

  /** Follows the pointer until it lifts, Escape ending it unsaved. */
  function follow(
    onMove: (event: globalThis.PointerEvent) => void,
    onUp: (event: globalThis.PointerEvent) => void,
  ) {
    stop.current?.();
    const up = (event: globalThis.PointerEvent) => {
      done();
      onUp(event);
    };
    const key = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        done();
        setLive(null);
        // Letting go after Escape is not a click on the bar either.
        swallowClick.current = true;
      }
    };
    const done = () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', up);
      window.removeEventListener('keydown', key);
      stop.current = null;
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', up);
    window.addEventListener('keydown', key);
    stop.current = done;
  }

  function trackProps(id: string) {
    return {
      'data-timeline-track': id,
      onPointerDown: (event: PointerEvent<HTMLElement>) => {
        const o = opts.current;
        if (!o.enabled || event.pointerType === 'touch' || event.button !== 0) return;
        // A press on a bar is the bar's.
        if ((event.target as Element).closest('[data-timeline-bar]')) return;
        const track = event.currentTarget;
        const startX = event.clientX;
        const anchor = fractionOn(track, startX);
        let drawing: GestureWindow | null = null;
        follow(
          (move) => {
            if (!drawing && Math.abs(move.clientX - startX) < SLOP_PX) return;
            drawing = drawnWindow(
              span(),
              o.zone,
              o.snapMinutes,
              o.shortestMinutes,
              anchor,
              fractionOn(track, move.clientX),
            );
            setLive({ kind: 'draw', track: id, window: drawing });
          },
          () => {
            setLive(null);
            if (drawing) opts.current.onDraw(id, drawing);
          },
        );
      },
    };
  }

  function barProps(id: string, window: GestureWindow, can: BarCan) {
    return {
      'data-timeline-bar': id,
      'data-draggable': opts.current.enabled && (can.move || can.resize) ? 'true' : undefined,
      onPointerDown: (event: PointerEvent<HTMLElement>) => {
        // A new press starts afresh: a release that was never a click leaves nothing to swallow.
        swallowClick.current = false;
        const o = opts.current;
        if (!o.enabled || event.pointerType === 'touch' || event.button !== 0) return;
        const bar = event.currentTarget;
        const track = bar.closest('[data-timeline-track]');
        if (!track) return;
        const rect = bar.getBoundingClientRect();
        const edge: Edge | null = !can.resize
          ? null
          : event.clientX - rect.left <= EDGE_PX
            ? 'start'
            : rect.right - event.clientX <= EDGE_PX
              ? 'end'
              : null;
        if (!edge && !can.move) return;
        const startX = event.clientX;
        let armed = false;
        let current: Gesture | null = null;
        const timer = globalThis.setTimeout(() => {
          armed = true;
          current = edge
            ? { kind: 'resize', id, edge, from: window, window }
            : { kind: 'move', id, from: window, window };
          setLive(current);
        }, HOLD_MS);
        follow(
          (move) => {
            if (!armed) {
              // Moved before the hold: a click or a scroll, never a drag.
              if (Math.abs(move.clientX - startX) >= SLOP_PX) globalThis.clearTimeout(timer);
              return;
            }
            const at = instantAt(span(), fractionOn(track, move.clientX));
            current = edge
              ? {
                  kind: 'resize',
                  id,
                  edge,
                  from: window,
                  window: resizedWindow(window, edge, at, o.zone, o.snapMinutes, o.shortestMinutes),
                }
              : {
                  kind: 'move',
                  id,
                  from: window,
                  window: movedWindow(
                    window,
                    at.epochMilliseconds -
                      instantAt(span(), fractionOn(track, startX)).epochMilliseconds,
                    o.zone,
                    o.snapMinutes,
                  ),
                };
            setLive(current);
          },
          () => {
            globalThis.clearTimeout(timer);
            setLive(null);
            if (!armed || !current) return;
            // The click that follows a drag is not a click on the bar.
            swallowClick.current = true;
            const g = current as Gesture;
            const same =
              g.kind !== 'draw' &&
              g.window.startsAt.equals(window.startsAt) &&
              g.window.endsAt.equals(window.endsAt);
            if (same) return;
            if (g.kind === 'move') opts.current.onMove(id, window, g.window);
            if (g.kind === 'resize') opts.current.onResize(id, g.edge, window, g.window);
          },
        );
      },
      onClickCapture: (event: MouseEvent<HTMLElement>) => {
        if (!swallowClick.current) return;
        swallowClick.current = false;
        event.preventDefault();
        event.stopPropagation();
      },
    };
  }

  return { live, trackProps, barProps };
}
